import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import QuickActionCard from "../../../../components/QuickActionCard/QuickActionCard";
import SecondaryLargeButton from "../../../../components/_utils/CommonButtons/SecondaryLargeButton/SecondaryLargeButton";
import TertiaryTopRoundButton from "../../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import CommonCardDashboard from "../../../../components/_utils/CommonCardDashboard";
import { remitterAccountViewAll } from "../../../../redux/features/accounts/remitterAccount/remitterAccount";
import { resetTaxToggle } from "../../../../redux/features/accounts/taxAccount/taxAccountViewAll";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { resetServices } from "../../../../redux/features/refreshToken/refreshToken";
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";
import globalText from "../../../../utils/globalText";
import { globalTextFormat } from "../../../../utils/globalTextFormat";

const RemitterAccountDashboard = () => {
  const divRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [hasScroll, setHasScroll] = useState(false);

  const requiredCreateService = "REUAccount";
  const approvalService = "REAAccount";
  const viewService = "REVAccount";

  const { remitterAccountViewAllData, remitterAccountViewAllLoading } =
    useSelector((state) => state.remitterAccount);

  const services = useSelector((state) => state.refreshToken.services) ?? [];
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const onMount = () => {
    dispatch(
      remitterAccountViewAll({
        page_no: GlobalConfiguration?.lengthCheckOne,
        page_size: PaginationConfiguration?.paginationSmall,
      }),
    );
  };

  useEffect(() => {
    dispatch(enableSwitcher());
    return () => {
      dispatch(disableSwitcher());
    };
  }, [dispatch]);

  useEffect(() => {
    onMount();
  }, [markerData]);

  const handleDatacardClick = (approvalStatus) => {
    dispatch(resetTaxToggle());

    if (approvalStatus === "pending") {
      if (services.includes("REAAccount") || services.includes("Admin")) {
        navigate("/remitter_account_dashboard/remitter_account_approval");
        return;
      }

      if (
        (services.includes("REVAccount") || services.includes("Admin")) &&
        !services.includes("REAAccount")
      ) {
        navigate("/remitter_account_dashboard/remitter_account_view_all", {
          state: { approvalStatus },
        });
        return;
      }

      navigate("/feature_not_assigned", {
        state: { unavailableService: "REAAccount" },
      });

      return;
    }

    navigate("/remitter_account_dashboard/remitter_account_view_all", {
      state: { approvalStatus },
    });
  };

  const summary = remitterAccountViewAllData?.summary || {};

  const hasCreatePermission =
    services?.includes(requiredCreateService) || services?.includes("Admin");

  const handleCreateRemitterAccount = () => {
    if (hasCreatePermission) {
      navigate("/remitter_account_dashboard/create_remitter_account");
      dispatch(resetServices());
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: requiredCreateService },
      });
    }
  };

  const isRequireApprovalFlow =
    services.includes(approvalService) || services.includes("Admin");

  const hasPendingApprovedService =
    services.includes(viewService) || services.includes("Admin");

  const approvedCount = hasPendingApprovedService
    ? summary.approved_accounts_count
    : "N/A";

  const handleRemitterServiceUnavailable = (service) => {
    navigate("/feature_not_assigned", {
      state: { unavailableService: service },
    });
  };

  const totalCount =
    remitterAccountViewAllData?.total_count ??
    GlobalConfiguration?.lengthCheckZero;

  const isViewAllDisabled =
    remitterAccountViewAllLoading ||
    totalCount === GlobalConfiguration?.lengthCheckZero;

  const handleViewAllClick = async () => {
    await dispatch(resetTaxToggle());

    const hasViewPermission =
      services.includes(viewService) || services.includes("Admin");

    if (hasViewPermission) {
      navigate("/remitter_account_dashboard/remitter_account_view_all", {
        state: { approvalStatus: "all" },
      });
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: viewService },
      });
    }
  };

  const checkScroll = () => {
    if (divRef.current) {
      const hasVerticalScroll =
        divRef.current.scrollHeight > divRef.current.clientHeight;
      const hasHorizontalScroll =
        divRef.current.scrollWidth > divRef.current.clientWidth;
      setHasScroll(hasVerticalScroll || hasHorizontalScroll);

      const atStart = divRef.current.scrollLeft === 0;
      const atEnd =
        Math.ceil(divRef.current.scrollLeft + divRef.current.clientWidth) >=
        divRef.current.scrollWidth;
      setIsAtStart(atStart);
      setIsAtEnd(atEnd);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => {
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  return (
    <div className=" internalDashboardView bg-primary-bg w-width-full overflow-y-auto px-padding-base pb-padding-md h-screen ">
      <div className="Qsmd:h-navbar-breadcrumb-height"></div>
      <div className="flex gap-x-gap-md flex-col mt-margin-md lg:mx-auto pb-padding-xl lg:pb-padding-base max-w-tab-width-normal w-width-full lg:flex-row px-padding-md Qlg:px-padding-base mb-margin-xl lg:mb-margin-base Qlg:relative">
        <div className="Qlg:w-dashboard-left-card-width">
          <div className="w-width-full flex flex-col pt-padding-md Qsmd:pt-padding-base mt-margin-xl Qsmd:mt-margin-base ">
            <div className="w-width-full p-padding-sm fixed top-top-base left-left-base right-right-base bg-text-primary flex items-center z-z-index-tooltip Qlg:hidden rounded-bl-border-radius-sm rounded-br-border-radius-sm">
              <div className="flex flex-row items-center">
                <img
                  src={ICONS?.REMITTER_ACCOUNT_WITH_BLUE_CIRCLE}
                  alt="REMITTER_ACCOUNT_ICONS"
                  className="h-icon-lg w-icon-lg mr-margin-sm"
                />
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <p className="text-primary-bg font-semibold text-font-md">
                      Remitter
                    </p>
                  </div>
                  <p className="text-primary-bg text-font-xs">Accounts</p>
                </div>
              </div>
            </div>
            <div className="w-width-full bg-primary-nav flex flex-col sm:flex-row rounded-border-radius-md items-center z-z-index-header gap-gap-sm">
              <div className="w-width-full flex">
                <SecondaryLargeButton
                  btnText="Create remitter account"
                  btnType="button"
                  handleFunction={handleCreateRemitterAccount}
                  isDisabled={!hasCreatePermission}
                  enabledTextColor="var(--text-primary)"
                  disabledTextColor="var(--text-secondary)"
                  enabledBgColor="var(--primary-nav)"
                  hoverOption="white"
                  isLoaderNeeded={false}
                  isLoading={false}
                  icon={ICONS?.ADD_BUTTON}
                  imgColor="var(--text-primary)"
                  iconAlt="add_button_white"
                />
              </div>
            </div>

            <div className="w-width-full rounded-border-radius-md relative overflow-hidden mt-margin-md">
              <h2 className="bg-primary-card flex items-center justify-between pl-padding-md transition-all duration-animation-medium">
                <div className="flex items-center gap-gap-sm">
                  <img
                    src={ICONS?.APPROVAL_STATUS}
                    alt="transaction header"
                    className="w-icon-sm"
                  />
                  <p className="text-font-sm font-semibold font-sans text-text-primary">
                    Accounts
                  </p>
                </div>
                <div className="flex">
                  <div className="border-dividing"></div>
                  <TertiaryTopRoundButton
                    btnText={globalText?.VIEW_ALL}
                    icon={ICONS?.ARROW_RIGHT_WHITE}
                    imgColor="var(--text-primary)"
                    btnType="button"
                    hoverOption="white"
                    handleFunction={() => {
                      if (isViewAllDisabled) return;
                      handleViewAllClick();
                      // toast.info("Feature coming soon!", toast_position);
                    }}
                    isDisabled={false}
                    enabledTextColor={"var(--text-primary)"}
                    disabledTextColor={"var(--text-secondary)"}
                    enabledBgColor={"var(--primary-card)"}
                    disabledBgColor={"var(--primary-act)"}
                  />
                </div>
              </h2>
              <div>
                <div className="flex flex-col Qsmd:flex-row gap-gap-base p-padding-base bg-primary-nav">
                  <CommonCardDashboard
                    label={
                      isRequireApprovalFlow
                        ? globalText?.REQUIRE_APPROVAL
                        : globalText?.PENDING_ACCOUNTS
                    }
                    count={summary?.pending_accounts_count ?? "00"}
                    // count="20"
                    color="var(--pending)"
                    loader={false}
                    onClick={() => handleDatacardClick("pending")}
                  />
                  <div className="border-border border-primary-bg Qsmd:my-margin-md my-margin-base"></div>
                  <CommonCardDashboard
                    label="Approved accounts"
                    count={approvedCount}
                    // count="10"
                    color="var(--awaiting)"
                    onClick={() => handleDatacardClick("approved")}
                    onServiceUnavailable={() =>
                      handleRemitterServiceUnavailable(viewService)
                    }
                    loader={false}
                  />
                </div>
              </div>
            </div>

            <div className="w-width-full mt-margin-md bg-primary-card rounded-t-border-radius-md rounded-b-border-radius-md">
              <h2 className="bg-primary-card flex items-center justify-between px-padding-md py-padding-sm text-sm font-semibold text-text-secondary transition-all duration-animation-medium rounded-t-border-radius-md">
                <span className="flex items-center text-sm font-semibold text-text-primary">
                  <img
                    src={ICONS?.STATISTICS}
                    alt="Statistics Header"
                    className="h-icon-sm mr-margin-sm"
                  />
                  Remitter accounts
                </span>
              </h2>
              <div className="border-border border-primary-bg mx-margin-md"></div>

              <div className="relative w-width-full lg:py-padding-md overflow-hidden contents lg:block ">
                <div className="flex flex-col items-center justify-center lg:justify-start h-height-full text-center p-padding-md">
                  <div className="flex flex-col justify-center items-center w-width-full">
                    <img
                      src={ICONS?.NO_STATISTICS}
                      alt="No Slides"
                      className="mb-margin-sm w-graphic-width-md h-graphic-rectangle-height-md flex-shrink-0"
                    />
                    <p className="text-font-xs text-text-primary">
                      No statistics available due to the absence of data. Once
                      data is present,
                      <br />
                      statistics will appear.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="Qlg:w-dashboard-right-card-width">
          <QuickActionCard
            title={globalTextFormat(globalText.MORE_ACTIONS, [0])}
            labels={"MIS report"}
            onClick={() => {
              if (services?.includes("SMisUser")) {
                navigate("/remitter_account_dashboard/generate_mis_report");
              } else {
                navigate("/feature_not_assigned", {
                  state: { unavailableService: "SMisUser" },
                });
              }
            }}
            labelDisabled={!services?.includes("SMisUser")}
          />
        </div>
      </div>
    </div>
  );
};

export default RemitterAccountDashboard;
